import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { entityHistory, type HistoryEntityType } from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';

import { Button } from './Button';
import { DataTable } from './DataTable';
import { DetailCard } from './DetailLayout';
import { EmptyState } from './EmptyState';
import { camposMudados, valorDoHistorico, type HistoryRow } from './historyValues';
import { Modal } from './Modal';
import { Pagination } from './Pagination';

export type { HistoryRow } from './historyValues';

function origemDe(linha: HistoryRow): string {
  if (linha.origin_hidden) return 'nao exibida';
  return linha.ip ? linha.ip : 'nao registrada';
}

export interface HistoryLabels {
  /** Nome da acao em portugues, como "Alteracao do limite". Padrao: a acao crua. */
  actionLabel?: ((action: string) => string) | undefined;
  /** Nome do campo em portugues. Padrao: a chave gravada. */
  fieldLabel?: ((field: string) => string) | undefined;
}

/** Dialogo "Visualizar alteracoes": dados, antes e depois, e a origem. */
export function HistoryChangeDialog({
  row,
  onClose,
  actionLabel = (acao) => acao,
  fieldLabel = (campo) => campo,
}: { row: HistoryRow | null; onClose: () => void } & HistoryLabels) {
  const mudados = row ? camposMudados(row) : [];
  const temOrigem =
    row !== null && (row.ip !== undefined || row.origin_hidden !== undefined);

  return (
    <Modal
      open={row !== null}
      onClose={onClose}
      title="Visualizar alteracoes"
      description={
        row
          ? `${actionLabel(row.action)} em ${formatDateTime(row.occurred_at)}`
          : undefined
      }
      size="lg"
    >
      {row ? (
        <div className="flex flex-col gap-4">
          <DataTable
            columns={[
              {
                key: 'dados',
                header: 'Dados',
                render: (campo: string) => fieldLabel(campo),
              },
              {
                key: 'antes',
                header: 'Antes',
                render: (campo: string) => valorDoHistorico(campo, row.before?.[campo]),
              },
              {
                key: 'depois',
                header: 'Depois',
                render: (campo: string) => valorDoHistorico(campo, row.after?.[campo]),
              },
            ]}
            items={mudados}
            keyOf={(campo) => campo}
            empty={<EmptyState title="Nenhum campo mudou nesta alteracao" />}
          />
          {temOrigem ? (
            <dl className="grid gap-3 text-xs sm:grid-cols-3">
              <div>
                <dt className="font-semibold text-ink-500">Origem (IP)</dt>
                <dd className="text-ink-900">{origemDe(row)}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="font-semibold text-ink-500">Navegador</dt>
                <dd className="break-all text-ink-900">
                  {row.origin_hidden
                    ? 'nao exibido'
                    : row.user_agent || 'nao registrado'}
                </dd>
              </div>
              {row.request_id ? (
                <div className="sm:col-span-3">
                  <dt className="font-semibold text-ink-500">Requisicao</dt>
                  <dd className="break-all font-mono text-2xs text-ink-700">
                    {row.request_id}
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

/** Tabela de historico com "Visualizar alteracoes", para quem ja tem as linhas. */
export function HistoryTable({
  rows,
  emptyText,
  showOrigin,
  actionLabel = (acao) => acao,
  fieldLabel,
}: {
  rows: HistoryRow[];
  emptyText: string;
  /** Mostra a coluna "Origem" (IP). A rota generica traz; as antigas, nao. */
  showOrigin?: boolean | undefined;
} & HistoryLabels) {
  const [aberta, setAberta] = useState<HistoryRow | null>(null);
  return (
    <>
      <DataTable
        columns={[
          {
            key: 'usuario',
            header: 'Usuario responsavel',
            render: (linha: HistoryRow) => linha.actor_label || 'sistema',
          },
          {
            key: 'data',
            header: 'Data da alteracao',
            render: (linha: HistoryRow) => formatDateTime(linha.occurred_at),
          },
          {
            key: 'evento',
            header: 'Evento',
            render: (linha: HistoryRow) => actionLabel(linha.action),
          },
          ...(showOrigin
            ? [
                {
                  key: 'origem',
                  header: 'Origem',
                  render: (linha: HistoryRow) => origemDe(linha),
                },
              ]
            : []),
          {
            key: 'acoes',
            header: 'Acoes',
            render: (linha: HistoryRow) => (
              <Button size="sm" variant="secondary" onClick={() => setAberta(linha)}>
                Visualizar alteracoes
              </Button>
            ),
          },
        ]}
        items={rows}
        keyOf={(linha) => linha.id}
        empty={<EmptyState icon="clock" title={emptyText} />}
      />
      <HistoryChangeDialog
        row={aberta}
        onClose={() => setAberta(null)}
        actionLabel={actionLabel}
        fieldLabel={fieldLabel}
      />
    </>
  );
}

export interface HistoryTabProps extends HistoryLabels {
  entityType: HistoryEntityType;
  entityId: string;
  title?: string | undefined;
  description?: string | undefined;
  /** Traz so as acoes que comecam pelo texto, como "tomadores.limite". */
  actionPrefix?: string | undefined;
  emptyText?: string | undefined;
  pageSize?: number | undefined;
}

/**
 * Aba "Historico de alteracoes" de qualquer cadastro (P-FUN-2b).
 *
 * Le `GET /api/v1/audit-logs/entities/{tipo}/{id}`: quem fez, quando, de onde e o
 * antes e o depois de cada alteracao. O servidor confere a permissao de leitura da
 * entidade, a empresa e o escopo da corretora; a tela so mostra.
 */
export function HistoryTab({
  entityType,
  entityId,
  title = 'Historico de Alteracoes',
  description = 'Toda mudanca no cadastro, com o antes e o depois.',
  actionPrefix,
  emptyText = 'Nenhuma alteracao registrada',
  pageSize = 25,
  actionLabel,
  fieldLabel,
}: HistoryTabProps) {
  const [offset, setOffset] = useState(0);
  const consulta = useQuery({
    queryKey: [
      'entity-history',
      entityType,
      entityId,
      actionPrefix ?? '',
      offset,
      pageSize,
    ],
    queryFn: ({ signal }) =>
      entityHistory.list(
        entityType,
        entityId,
        {
          limit: pageSize,
          offset,
          ...(actionPrefix ? { action: actionPrefix } : {}),
        },
        signal,
      ),
  });

  return (
    <DetailCard
      title={title}
      description={description}
      loading={consulta.isPending}
      error={consulta.error}
    >
      <div className="flex flex-col">
        <HistoryTable
          rows={consulta.data?.items ?? []}
          emptyText={emptyText}
          showOrigin
          actionLabel={actionLabel}
          fieldLabel={fieldLabel}
        />
        {consulta.data ? (
          <Pagination
            total={consulta.data.total}
            limit={pageSize}
            offset={offset}
            onChange={setOffset}
          />
        ) : null}
      </div>
    </DetailCard>
  );
}
